import { rest } from "msw";
import type { EquipmentAdapterType } from "../adapters/EquipmentAdapters.ts";
import type { DomainConfig } from "../config.ts";
import {
  EquipmentSchema,
  EquipmentStatus,
  type EquipmentType,
} from "../schemas/EquipmentSchemas.ts";
import {
  getApiUrlWithEndpoint,
  type BuildMockHandlersType,
} from "./handlers.ts";
import { DomainFaker, DomainMock } from "./utils.ts";

export const EquipmentMock = new DomainMock<
  EquipmentType,
  typeof EquipmentSchema
>(
  EquipmentSchema,
  () => {
    const createdAt = DomainFaker.en.date.past();
    const typeId = DomainFaker.en.string.uuid();
    const facilityId = DomainFaker.en.string.uuid();

    return {
      id: DomainFaker.en.string.uuid(),
      name: DomainFaker.en.vehicle.manufacturer(),
      type_id: typeId,
      type: {
        id: typeId,
        name: DomainFaker.en.vehicle.model(),
      },
      status: DomainFaker.en.helpers.arrayElement(
        Object.values(EquipmentStatus)
      ),
      latitude: DomainFaker.en.number.float({ min: -90, max: 90 }),
      longitude: DomainFaker.en.number.float({ min: -180, max: 180 }),
      custom_attributes: {},
      company_id: DomainFaker.en.string.uuid(),
      effective_date: DomainFaker.en.date
        .between({
          from: createdAt,
          to: new Date(),
        })
        .toISOString(),
      facility_id: facilityId,
      facility: {
        id: facilityId,
        name: DomainFaker.en.vehicle.manufacturer(),
      },
      created_at: createdAt.toISOString(),
      created_by: DomainFaker.en.string.uuid(),
      updated_at: DomainFaker.en.date
        .between({ from: createdAt, to: new Date() })
        .toISOString(),
      updated_by: DomainFaker.en.string.uuid(),
    };
  },
  true
).fix();

export const buildEquipmentHandlers: BuildMockHandlersType<
  EquipmentAdapterType
> = (configData: DomainConfig) => {
  const { nodeURL: url } = configData;

  return {
    getList: rest.post(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/equipment/search",
      }),
      (req, res, ctx) => {
        const page = Number(req.url.searchParams.get("page"));
        const pageSize = Number(req.url.searchParams.get("pageSize"));
        const data = new Array(5).fill(0).map(() => EquipmentMock.generate());

        return res(
          ctx.status(200),
          ctx.json({
            data,
            page_number: page,
            page_size: pageSize,
            total_entries: data.length,
            total_pages: 1,
          })
        );
      }
    ),
    getOne: rest.get(
      getApiUrlWithEndpoint({
        url,
        version: 1,
        endpoint: "/equipment/:id",
      }),
      (req, res, ctx) => {
        const { id } = req.params;

        if (typeof id !== "string") {
          return res(ctx.status(400));
        }

        const mockData = {
          ...EquipmentMock.mock,
          id,
        };

        return res(ctx.status(200), ctx.json(mockData));
      }
    ),
  };
};
